import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, CalendarDays, Wrench, ClipboardCheck,
  Users, Settings, LogOut, Building2, Globe, ShieldAlert, Coins,
  LayoutGrid, FileText, Bell, Mail, HelpCircle
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import './Sidebar.css';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/time-off', label: 'Time Off', icon: Calendar },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/tools', label: 'Tools', icon: Wrench },
  { path: '/admin/tools', label: 'Manage Tools', icon: Settings, requiresRole: ['admin', 'manager'] },
];

const adminItems = [
  { path: '/admin/users', label: 'Team', icon: Users },
  { path: '/admin/allocations', label: 'Allocations', icon: Coins },
  { path: '/admin/departments', label: 'Departments', icon: Building2 },
  { path: '/admin/holidays', label: 'Holidays', icon: Globe },
  { path: '/admin/blackout-dates', label: 'Gray Dates', icon: ShieldAlert },
  { path: '/admin/modules', label: 'Modules', icon: LayoutGrid },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
  { path: '/admin/audit-log', label: 'Audit Log', icon: FileText },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell },
  { path: '/admin/email-templates', label: 'Email Templates', icon: Mail },
];

function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [hasHolidayCountries, setHasHolidayCountries] = useState(false);

  // Check if this manager has holiday country assignments (show Holidays link)
  useEffect(() => {
    if (user?.role === 'manager') {
      api.get('/users/me/holiday-countries')
        .then(res => setHasHolidayCountries((res.holiday_countries || []).length > 0))
        .catch(() => {});
    }
  }, [user?.role]);

  const displayName = user?.name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const isAdmin = user?.role === 'admin';
  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'manager';
  const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '';

  const logoSrc = branding.sidebar_logo || '/logo-light.svg';

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    logout();
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <>
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src={logoSrc} alt={branding.app_name || 'FPS OS'} className="sidebar-logo-img" />
          <span className="logo-text">{branding.app_name || 'FPS OS'}</span>
        </div>

        <nav className="sidebar-nav">
          {navItems
            .filter(({ requiresRole }) => !requiresRole || requiresRole.includes(user?.role))
            .map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `nav-item ${isActive || location.pathname.startsWith(path) ? 'active' : ''}`
              }
            >
              <Icon size={18} strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}

          {isManagerOrAdmin && (
            <NavLink
              to="/approvals"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
            >
              <ClipboardCheck size={18} strokeWidth={2} />
              <span>Approvals</span>
            </NavLink>
          )}

          {!isAdmin && hasHolidayCountries && (
            <NavLink
              to="/admin/holidays"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <Globe size={18} strokeWidth={2} />
              <span>Holidays</span>
            </NavLink>
          )}

          {isAdmin && (
            <>
              <div className="sidebar-section-label">Admin</div>
              {adminItems.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) =>
                    `nav-item ${isActive || location.pathname.startsWith(path) ? 'active' : ''}`
                  }
                >
                  <Icon size={18} strokeWidth={2} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <NavLink to="/help" className={({ isActive }) => `nav-item nav-item-help ${isActive ? 'active' : ''}`}>
            <HelpCircle size={18} strokeWidth={2} />
            <span>Help</span>
          </NavLink>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{displayName}</span>
              <span className="user-role">{roleLabel}</span>
            </div>
            <button className="logout-btn" onClick={handleLogoutClick} title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="logout-modal-overlay" onClick={handleLogoutCancel}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Sign Out?</h3>
            <p>Are you sure you want to sign out of FPS OS?</p>
            <div className="logout-modal-actions">
              <button className="btn-cancel" onClick={handleLogoutCancel}>Cancel</button>
              <button className="btn-danger" onClick={handleLogoutConfirm}>
                <LogOut size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
